import React, { useState, useEffect, useRef } from "react";
import { InputGroup, Table, Container, Row, Col, Button, Form, ButtonGroup } from 'react-bootstrap';
import '../css/c4fsimpletable.css';
import { getImageFlagForC4FCode, formatCurrency } from "./Helpers";


function C4FSimpleTable(props) {
    const mounted = useRef(false);
    const [data, setData] = useState(props.data);
    const [filters, setFilters] = useState('filters' in props ? props.filters : []);
    const [showFilter, setShowFilter] = useState(false);
    const [sortBy, setSortBy] = useState('initialSortingColumn' in props ? props.initialSortingColumn : null);
    const [ascending, setAscending] = useState('initialSortingAscending' in props ? props.initialSortingAscending : false);
    const [columnVisible, setColumnVisible] = useState([]);
    const [columnGroups, setColumnGroups] = useState([]);
    const [editColumnVisibility, setEditColumnVisibility] = useState(false);
    const [maxHeight, setMaxHeight] = useState('maxHeight' in props ? props.maxHeight : 520);

    function getColumnStyle(columnType, value) {
        if (columnType === 'energy')
            return formatEnergy(value);
        else if (columnType === 'highlightedCurrency') {
            if (value > 0) return { textAlign: 'right', color: '#007e00' }
            if (value < 0) return { textAlign: 'right', color: '#940000' }
            return { textAlign: 'right' }
        }
        else if (columnType === 'fairplayVote') {
            let color = '#000000';
            switch (value) {
                case -3: color = '#940000';break;
                case -2: color = '#e43415';break;
                case -1: color = '#ffa600';break;
                case 1: color = '#0db30d';break;
                case 2: color = '#069c06';break;
                case 3: color = '#007e00';break;
            }
            return { textAlign: 'center', fontWeight: 'bold', color: color }
        }
    }

    function formatEnergy(value) {
        if (value > 800)
            return { fontWeight: 'bold', color: 'rgba(0, 156, 34, 1)' };
        if (value > 600)
            return { fontWeight: 'bold', color: 'rgba(0, 114, 184, 1)' };
        if (value > 400)
            return { fontWeight: 'bold', color: 'rgba(243, 149, 0, 1)' };
        if (value > 200)
            return { fontWeight: 'bold', color: 'rgba(243, 83, 0, 1)' };
        else
            return { fontWeight: 'bold', color: 'rgba(203, 6, 0, 1)' };
    }

    function highlightRecord(record) {
        if ('highlightCriteriaKey' in props && props.highlightCriteriaKey !== null) {
            if (record[props.highlightCriteriaKey] === props.highlightCriteriaValue)
                return true;
        }
        return false;
    }

    useEffect(() => {
        mounted.current = true;
    }, []);

    useEffect(() => {
        return () => {
            mounted.current = false;
        }
    }, []);

    useEffect(() => {
        setFilters('filters' in props ? props.filters : []);
    }, [props.filters]);

    useEffect(() => {
        if (mounted.current && props.data.length > 0 || ('rowMetadata' in props && props.rowMetadata.length > 0)) {
            let dataLocal = props.data;
            let columnVisibleLocal = [];
            let columnGroupsLocal = [];

            // check, if metadata matches data
            if (columnVisible.length > 0) {
                let metadataComplete = true;
                // check all columns
                if ('rowMetadata' in props && props.rowMetadata.length > 0) {
                    props.rowMetadata.forEach(md => {
                        if (!columnVisible.some(cv => cv.columnName === md.columnName))
                            metadataComplete = false;
                    });
                    columnVisible.forEach(cv => {
                        if (!props.rowMetadata.some(md => cv.columnName === md.columnName))
                            metadataComplete = false;
                    });
                }

                if (metadataComplete) {
                    columnVisibleLocal = columnVisible;
                    columnGroupsLocal = columnGroups;
                }
            }

            // set defaults ...
            props.rowMetadata.forEach(md => {
                // guess the data type
                let defaultValue = 0;
                if (dataLocal.some(record => typeof record[md.columnName] === 'string'))
                    defaultValue = '';

                dataLocal = dataLocal.map(record => {
                    if (!(md.columnName in record) || record[md.columnName] === null)
                        record[md.columnName] = defaultValue;
                    return record;
                });
            })

            if (columnVisibleLocal.length === 0) {
                let propsColumnVisibleExists = 'columnVisible' in props;
                if ('rowMetadata' in props && props.rowMetadata.length > 0) {
                    props.rowMetadata.forEach(md => {
                        let columnVisible = propsColumnVisibleExists && props.columnVisible.some(column => column.columnName === md.columnName) ?
                            props.columnVisible.find(column => column.columnName === md.columnName).visible : true;
                        columnVisibleLocal.push({ columnName: md.columnName, visible: columnVisible });
                    });
                }
                else
                    Object.keys(props.data[0]).forEach(key => columnVisibleLocal.push({ columnName: key, visible: true }));

                setColumnVisible(columnVisibleLocal);
            }

            if (columnGroupsLocal.length === 0) {
                setColumnGroups(calculateColumnGroups(columnVisibleLocal));
            }

            setData(dataLocal);
        }

    }, [props.data, props.rowMetadata]);

    function calculateColumnGroups(columnVisible) {
        let columnGroupsLocal = [];
        props.rowMetadata.forEach(md => {
            if (columnGroupsLocal.some(cg => md.columnGroup === cg.name)) {
                let key = columnGroupsLocal.findIndex(cg => md.columnGroup === cg.name)
                columnGroupsLocal[key].totalSize++;
                if (columnVisible.some(column => column.columnName && column.visible))
                    columnGroupsLocal[key].currentSize++;
            }
            else
                columnGroupsLocal.push({ name: md.columnGroup, totalSize: 1, currentSize: 1, visible: true });

            return;
        });
        if ('indexColumn' in props) {
            columnGroupsLocal[0].totalSize++;
            columnGroupsLocal[0].currentSize++;
        }
        return columnGroupsLocal;
    }

    function handleColumnVisibility(columnName) {
        // if columnGroups exist
        if (columnGroups.length > 0) {
            let columnGroupsLocal = columnGroups;
            // find in metadata
            let md = props.rowMetadata.find(md => md.columnName === columnName);
            let index = columnGroupsLocal.findIndex(cg => cg.name === md.columnGroup);
            // adjust columnGroup size
            columnGroupsLocal[index].currentSize += (columnVisible.find(col => col.columnName === columnName).visible ? -1 : 1);
            setColumnGroups(columnGroupsLocal);

        }

        setColumnVisible(
            columnVisible
                .filter(col => col.columnName !== columnName)
                .concat(
                    {
                        columnName: columnName,
                        visible: !columnVisible.find(col => col.columnName === columnName).visible
                    }));
    }

    function handleColumnGroupVisibility(columnGroup, visible) {
        let columnGroupsLocal = columnGroups;
        let changedColumnGroup = columnGroupsLocal.findIndex(cg => cg.name === columnGroup);
        let columnVisibleLocal = [];
        props.rowMetadata
            .filter(md => md.columnGroup === columnGroup)
            .forEach(md => {
                columnVisibleLocal.push({ columnName: md.columnName, visible: visible });
                if (columnVisible.find(col => col.columnName === md.columnName).visible !== visible) {
                    columnGroupsLocal[changedColumnGroup].currentSize += (columnVisible.find(col => col.columnName === md.columnName).visible ? -1 : 1);
                    columnGroupsLocal[changedColumnGroup].visible = visible;
                }
            });

        setColumnGroups(columnGroupsLocal);

        setColumnVisible(
            columnVisible
                .filter(col => props.rowMetadata.some(md => md.columnName === col.columnName && md.columnGroup !== columnGroup))
                .concat(columnVisibleLocal));
    }

    let rowMetadata = 'rowMetadata' in props ? props.rowMetadata : [];

    function setFilter(columnName, type, value) {
        let filtersKeep = filters.filter(filter => filter.columnName !== columnName || filter.type !== type);
        if (value === '' || value === null) setFilters(filtersKeep);
        else {
            try {
                setFilters(filtersKeep.concat({
                    columnName: columnName,
                    type: type,
                    value: value,
                    regex: type === 'text' ? new RegExp(value, 'i') : null,
                    valid: type === 'from' || type === 'to' ? !isNaN(value) : true
                }));
            }
            catch (e) {
                setFilters(filtersKeep.concat({ columnName: columnName, type: type, value: value, regex: null, valid: false }));
            }
        }
    }

    function getFilter(columnName, type) {
        let filter = filters.find(filter => filter.columnName === columnName && filter.type === type);
        if (filter === undefined) return { value: '', valid: true };
        else return filter;
    }

    function isIncluded(record) {
        let isIncluded = true;
        Object.keys(record).forEach(key => {
            if (!isIncluded) return;
            if (typeof record[key] === 'string') {
                // date filters (to and from)
                if (record[key].match(/[0-9]{2}-[0-9]{2}-[0-9]{2}/)) {
                    let fromFilter = filters.find(filter => filter.columnName === key && filter.type === 'from');
                    if (fromFilter !== undefined && record[key].localeCompare(fromFilter.value) < 0) isIncluded = false;

                    let toFilter = filters.find(filter => filter.columnName === key && filter.type === 'to')
                    if (toFilter !== undefined && toFilter.value.localeCompare(record[key]) < 0) isIncluded = false;
                }
                // simple text filters
                else {
                    let textFilter = filters.find(filter => filter.columnName === key && filter.type === 'text');
                    if (textFilter === undefined) return;
                    if (!record[key].match(textFilter.regex)) isIncluded = false;
                }
            }
            // filters on numbers (to and from)
            else {
                let fromFilter = filters.find(filter => filter.columnName === key && filter.type === 'from');
                if (fromFilter !== undefined && record[key] < fromFilter.value) isIncluded = false;

                let toFilter = filters.find(filter => filter.columnName === key && filter.type === 'to')
                if (toFilter !== undefined && record[key] > toFilter.value) isIncluded = false;
            }
        });
        return isIncluded;
    }

    function handleSorting(sortByNew) {
        setAscending(sortBy !== sortByNew ? false : !ascending);
        setSortBy(sortByNew);
    }

    function setAllColumnsVisible() {
        let columnVisibleLocal = columnVisible.map(column => ({ ...column, visible: true }));
        setColumnGroups(calculateColumnGroups(columnVisibleLocal));
        setColumnVisible(columnVisibleLocal);
    }

    if (typeof data !== 'undefined' && data !== null && (data.length > 0 || 'rowMetadata' in props && props.rowMetadata.length > 0) && columnVisible.length > 0)
        return <>
            {props.data.constructor === Array ?
                <>
                    <Container className="container m-0 p-0">
                        {!props.hideFilterButtons ?
                            <Row className="c4f-stats m-0 p-0">
                                <Col className=" m-0 mb-1 p-0 text-left">
                                    <ButtonGroup>
                                        {'addRecord' in props ?
                                            (props.addRecord !== null ?
                                                <Button variant="success py-0" onClick={() => props.addRecord()}>+</Button>
                                                :
                                                <Button variant="outline-success py-0" disabled>+</Button>
                                            )
                                            :
                                            null
                                        }
                                        {'deleteRecord' in props ?
                                            (props.deleteRecord !== null ?
                                                <Button variant="danger py-0" onClick={() => props.deleteRecord()}>X</Button>
                                                :
                                                <Button variant="outline-danger py-0" disabled>X</Button>
                                            )
                                            :
                                            null
                                        }
                                        {showFilter ?
                                            <Button variant="outline-primary py-0" onClick={(e) => setShowFilter(false)}>Filters {'\u25B2'}</Button>
                                            :
                                            <Button variant="primary py-0" onClick={(e) => setShowFilter(true)}>Filters {'\u25BC'}</Button>
                                        }
                                        {filters && filters.length === 0 ?
                                            null
                                            :
                                            <Button className="py-0" variant="danger" onClick={(e) => setFilters([])}>X</Button>
                                        }
                                        {'refreshTable' in props ?
                                            <Button className="py-0" variant="success" onClick={(e) => props.refreshTable()}>{'\u21BB'}</Button>
                                            :
                                            null
                                        }
                                    </ButtonGroup>
                                </Col>
                                <Col className=" m-0 mb-1 p-0 text-right">
                                    <ButtonGroup>
                                        {!props.hideColumnVisibility ?
                                            <>
                                                <Button variant={editColumnVisibility ? 'success' : 'secondary'} className="py-0" onClick={(e) => setEditColumnVisibility(!editColumnVisibility)}>
                                                    {editColumnVisibility ? 'Save' : 'Edit Cols'}
                                                </Button>
                                                {columnVisible.some(column => !column.visible) ?
                                                    <Button variant={'success'} className="py-0" onClick={(e) => setAllColumnsVisible()}>Show All</Button>
                                                    :
                                                    <Button variant={'outline-success'} className="py-0" disabled>Show All</Button>
                                                }
                                            </>
                                            : null
                                        }
                                        <Button disabled variant="outline-secondary py-0">{data.filter(isIncluded).length + ' / ' + data.length + ' rows'}</Button>
                                        <Button disabled variant="outline-secondary py-0">{columnVisible.filter(col => col.visible).length + ' / ' + columnVisible.length + ' cols'}</Button>
                                    </ButtonGroup>
                                </Col>
                            </Row>
                            : null}
                        <Row className="row d-flex c4f-stats m-0 p-0">
                            {showFilter ?
                                <Col className=" m-0 p-0" style={{ minWidth: 300, maxWidth: 300, maxHeight: maxHeight }}>
                                    {rowMetadata.filter(md => md.columnType !== 'button').map(md =>
                                        <InputGroup>
                                            <Form.Control
                                                size="sm"
                                                disabled
                                                value={'columnDescription' in md ? md.columnDescription : md.columnName}
                                            />
                                            {typeof data[0][md.columnName] === 'string' && !data[0][md.columnName].match(/[0-9]{2}-[0-9]{2}-[0-9]{2}/) ?
                                                <Form.Control className={getFilter(md.columnName, 'text').valid ? null : 'is-invalid'}
                                                    size="sm"
                                                    type={'text'}
                                                    placeholder={'name'}
                                                    onChange={(e) => setFilter(md.columnName, 'text', e.target.value)}
                                                    value={getFilter(md.columnName, 'text').value}
                                                />
                                                :
                                                <>
                                                    <Form.Control className={getFilter(md.columnName, 'from').valid ? null : 'is-invalid'}
                                                        size="sm"
                                                        type={typeof data[0][md.columnName] === 'string' && data[0][md.columnName].match(/[0-9]{2}-[0-9]{2}-[0-9]{2}/) ? 'date' : 'text'}
                                                        placeholder={'from'}
                                                        onChange={(e) => setFilter(md.columnName, 'from', e.target.value)}
                                                        value={getFilter(md.columnName, 'from').value}
                                                    />
                                                    <Form.Control className={getFilter(md.columnName, 'to').valid ? null : 'is-invalid'}
                                                        size="sm"
                                                        type={typeof data[0][md.columnName] === 'string' && data[0][md.columnName].match(/[0-9]{2}-[0-9]{2}-[0-9]{2}/) ? 'date' : 'text'}
                                                        placeholder={'to'}
                                                        onChange={(e) => setFilter(md.columnName, 'to', e.target.value)}
                                                        value={getFilter(md.columnName, 'to').value}
                                                    />
                                                </>
                                            }
                                        </InputGroup>
                                    )}
                                </Col>
                                : null}
                            <Col className={(showFilter ? ' ml-1 p-0' : ' m-0 p-0') + ' c4f-stats'} style={{ minWidth: 200, maxHeight: maxHeight, overflow: "auto" }}>
                                <Table className="c4f-stats-table border border-light m-0 p-0" key={'C4FSimpleTable' + sortBy + ascending} striped size="sm">
                                    <thead className="c4f-stats-thead">
                                        {rowMetadata.some(md => 'columnGroup' in md) ?
                                            <tr >
                                                {editColumnVisibility ?
                                                    columnGroups.map(cg =>
                                                        <th className={'c4f-stats-th1 c4f-stats-th-editColumns ' +
                                                            (cg.currentSize > 0 ? '' : ' c4f-stats-th-hide')}
                                                            colSpan={cg.totalSize}
                                                            onClick={() => handleColumnGroupVisibility(cg.name, !cg.visible)}
                                                        >{cg.name}</th>)
                                                    :
                                                    columnGroups.map(cg =>
                                                        <>
                                                            {
                                                                cg.currentSize > 0 ?
                                                                    <th className={'c4f-stats-th1'}
                                                                        colSpan={cg.currentSize}
                                                                    >{cg.name}</th>
                                                                    : null
                                                            }
                                                        </>)
                                                }
                                            </tr>
                                            : null
                                        }
                                        <tr>
                                            {'indexColumn' in props ?
                                                <th className="c4f-stats-th2">#</th>
                                                :
                                                null
                                            }
                                            {rowMetadata.length === 0 ?
                                                Object.keys(data[0]).map(key =>
                                                (
                                                    editColumnVisibility ?
                                                        <th className={'c4f-stats-th2 c4f-stats-th-editColumns ' + (columnVisible.some(col => col.columnName === key) && columnVisible.find(col => col.columnName === key).visible ? '' : ' c4f-stats-th-hide')}
                                                            onClick={() => handleColumnVisibility(key)}                                                            >
                                                            {key}
                                                        </th>
                                                        : (columnVisible.some(col => col.columnName === key) && columnVisible.find(col => col.columnName === key).visible ?
                                                            <th className="c4f-stats-th2"
                                                                onClick={() => handleSorting(key)}>
                                                                {key} {sortBy === key ? (ascending ? '\u2191' : '\u2193') : null}
                                                            </th>
                                                            :
                                                            null
                                                        )
                                                ))
                                                :
                                                rowMetadata.map(md => (
                                                    editColumnVisibility ?
                                                        <th className={'c4f-stats-th2 c4f-stats-th-editColumns ' + (columnVisible.some(col => col.columnName === md.columnName) && columnVisible.find(col => col.columnName === md.columnName).visible ? '' : ' c4f-stats-th-hide')}
                                                            onClick={() => handleColumnVisibility(md.columnName)}>
                                                            {'columnHeader' in md ?
                                                                md.columnHeader
                                                                :
                                                                ('columnDescription' in md ?
                                                                    md.columnDescription
                                                                    :
                                                                    md.columnName
                                                                )
                                                            }
                                                            {/* {columnVisible.find(col => col.columnName === md.columnName).visible ? '\uD83D\uDFE2' : '\uD83D\uDD34'} */}
                                                        </th>
                                                        :
                                                        (columnVisible.some(col => col.columnName === md.columnName) &&
                                                            columnVisible.find(col => col.columnName === md.columnName).visible ?
                                                            <th className="c4f-stats-th2" onClick={() => handleSorting(md.columnName)}>
                                                                {'columnHeader' in md ?
                                                                    md.columnHeader
                                                                    :
                                                                    ('columnDescription' in md ?
                                                                        md.columnDescription
                                                                        :
                                                                        md.columnName
                                                                    )
                                                                }
                                                                {sortBy === md.columnName ? (ascending ? '\u2191' : '\u2193') : null}
                                                            </th>
                                                            :
                                                            null
                                                        )
                                                ))
                                            }
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data
                                            .filter(isIncluded)
                                            .sort(function (a, b) {
                                                if (sortBy === null) return 0;
                                                if (typeof a[sortBy] === 'string' || typeof b[sortBy] === 'string') return ascending ?
                                                    a[sortBy].toString().localeCompare(b[sortBy]) : 
                                                    b[sortBy].toString().localeCompare(a[sortBy]);
                                                else return ascending ?
                                                    a[sortBy] - b[sortBy] : 
                                                    b[sortBy] - a[sortBy];
                                            })
                                            .map((record, index) =>
                                                <tr >
                                                    {'indexColumn' in props ?
                                                        <td className={'c4f-stats-td-index'}>{index + 1}</td>
                                                        :
                                                        null
                                                    }
                                                    {
                                                        rowMetadata.length === 0 ?
                                                            Object.keys(record).map(key =>
                                                                columnVisible.some(col => col.columnName === key) && columnVisible.find(col => col.columnName === key).visible || editColumnVisibility ?
                                                                    <td className={typeof record[key] !== 'string' ? 'c4f-stats-td-number' : 'c4f-stats-td-text'}>
                                                                        {typeof record[key] !== 'string' && record[key] % 1 !== 0 ? Math.round(record[key] * 100) / 100 : record[key]}
                                                                    </td>
                                                                    :
                                                                    null)
                                                            :
                                                            rowMetadata.map(md =>
                                                                columnVisible.some(col => col.columnName === md.columnName) && columnVisible.find(col => col.columnName === md.columnName).visible || editColumnVisibility ?
                                                                    <>
                                                                        {md.columnType === 'button' ?
                                                                            <td title={md.columnName} className={'c4f-stats-td-number'}>
                                                                                {'cellClickEvent' in md && md.cellClickEvent !== null ?
                                                                                    <Button className={'className' in md ? md.className : 'outline-primary'} size="sm"
                                                                                        onClick={() => md.cellClickEvent(record)}>
                                                                                        {record[md.columnName]}
                                                                                    </Button>
                                                                                    : null}
                                                                            </td>
                                                                            : null
                                                                        }
                                                                        {md.columnType === 'object' ?
                                                                            <td title={md.columnName} className={md.columnAlign && md.columnAlign === 'left' ? 'c4f-stats-td-text' : 'c4f-stats-td-number'}>
                                                                                {record[md.columnName]}
                                                                            </td>
                                                                            : null
                                                                        }
                                                                        {md.columnType === 'flag' ?
                                                                            (record[md.columnName] !== '' ?
                                                                                <td title={md.columnName} className={'c4f-stats-td-number'}>
                                                                                    {getImageFlagForC4FCode(record[md.columnName])}
                                                                                </td>
                                                                                :
                                                                                <td className={'c4f-stats-td-text'}></td>
                                                                            )
                                                                            : null
                                                                        }
                                                                        {md.columnType !== 'flag' && md.columnType !== 'object' && md.columnType !== 'button' ?
                                                                            <td title={md.columnName} className={
                                                                                (typeof record[md.columnName] !== 'string' ? 'c4f-stats-td-number' : 'c4f-stats-td-text') +
                                                                                ('cellClickEvent' in md && md.cellClickEvent !== null ? ' c4f-stats-td-clickable' : '') +
                                                                                (highlightRecord(record) ? ' c4f-stats-td-highlight' : '')}
                                                                                style={getColumnStyle(md.columnType, record[md.columnName])}
                                                                                onClick={'cellClickEvent' in md && md.cellClickEvent !== null ? () => md.cellClickEvent(record) : null}>
                                                                                {md.columnType === 'currency' || md.columnType === 'highlightedCurrency' ?
                                                                                    formatCurrency(record[md.columnName])
                                                                                    : null}
                                                                                {!['currency', 'highlightedCurrency'].includes(md.columnType) ?
                                                                                    (md.columnName in record && typeof record[md.columnName] !== 'string' && record[md.columnName] % 1 !== 0 ?
                                                                                        (Math.round(record[md.columnName] * 100000) / 100000).toLocaleString("en-EN", { minimumFractionDigits: 'digits' in md ? md.digits : 1, maximumFractionDigits: 'digits' in md ? md.digits : 1 })
                                                                                        :
                                                                                        (md.columnName in record && record[md.columnName] !== null ?
                                                                                            record[md.columnName].toLocaleString("en-EN", { maximumFractionDigits: 1 })
                                                                                            :
                                                                                            null)
                                                                                    )
                                                                                    : null}
                                                                            </td>
                                                                            : null
                                                                        }
                                                                    </>
                                                                    : null
                                                            )

                                                    }
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    </Container>
                </>
                : null}
        </>
    else return null;
}

export default C4FSimpleTable;
